import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { schedulingStats } from '@/data/scheduling-rules';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Download,
  Edit3,
  FileSpreadsheet,
  FileText,
  HelpCircle,
  Image,
  Info,
  MoreHorizontal,
  Save,
  Search,
  Settings,
  Share,
  TrendingUp,
  Users,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';

export function MentalModelView() {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [configMode, setConfigMode] = useState(false);

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Detailed rules organized by JTBD
  const detailedRules = {
    troubleshoot: {
      title: 'Troubleshooting Common Issues',
      rules: [
        {
          category: 'Appointment Type Not Schedulable',
          rule: 'Iris can only book appointment types that Trapeze can handle',
          explanation:
            'If Iris cannot ever schedule an appointment type, it will offer to transfer to the PCC or the office depending on their preference',
        },
        {
          category: 'Patient Request vs. A&P Conflict',
          rule: 'If the patient has a specific request that conflicts with A&P, respect the appointment type that the patient requested',
          explanation: 'Patient autonomy takes priority over clinical recommendations from A&P',
        },
        {
          category: 'Emergency Symptoms',
          rule: 'If patient indicates specific urgency or symptoms outlined in common emergency symptoms, select Emergency',
          explanation:
            'Emergency appointments require immediate escalation and cannot be handled by Iris',
        },
      ],
    },
    capacity: {
      title: 'Understanding Scheduling Capacity',
      rules: [
        {
          category: 'Appointment Type Determination',
          rule: 'Iris takes into account: Patient conversation, A&P, and set of possible appointment types',
          explanation:
            'The decision hierarchy balances patient requests, clinical needs, and system capabilities',
        },
        {
          category: 'New vs Existing Patients',
          rule: "If patient has no A&P and doesn't clarify need/request, book existing patient as follow up",
          explanation: 'Default behavior for existing patients without specific needs',
        },
        {
          category: 'New Patient Handling',
          rule: 'If patient is a new patient and has a specific request, book with one of consult types. If no specific request, book as a general NPT',
          explanation: 'New patients get specialized handling based on their stated needs',
        },
      ],
    },
    configuration: {
      title: 'Rule Configuration Details',
      rules: [
        {
          category: 'Template Label Matching',
          rule: 'Iris will read iMW provider schedule, including each scheduling slot and the template labels for available slots',
          explanation:
            'If the labels have specific requirements around what kind of appointments are a fit, Iris respects them. Iris can always book appointments in slots that match the template labels',
        },
        {
          category: 'Urgent Request Rules',
          rule: "If request is for 'earliest' or date within 7 days and there are empty slots within the next 7 days, book appointments in any slot still available",
          explanation:
            "Emergency timeframe overrides normal slot restrictions (but not emergency-only slots, as Iris doesn't handle emergency appointments)",
        },
        {
          category: 'Long Appointment Constraints',
          rule: 'Long appointments cannot be scheduled back to back, 30 mins before lunch, or 1 hr before end of day',
          explanation: 'Provider needs adequate buffer time for complex appointments',
        },
        {
          category: 'Testing Appointments',
          rule: "Testing appointments cannot be scheduled in the last 30 minutes of the provider's day",
          explanation: 'Tests require sufficient time for completion and cleanup',
        },
      ],
    },
    investigation: {
      title: 'Specific Type Investigation',
      rules: [
        {
          category: 'Patient Request Priority',
          rule: 'If patient says an appointment type and does not have A&P, respect their appointment type',
          explanation:
            'Patient-stated preferences take precedence when no clinical guidance exists',
        },
        {
          category: 'Vague Request Handling',
          rule: "If patient has a general/unspecific request that doesn't follow an appointment type, follow A&P",
          explanation: 'Clinical assessment guides appointment selection for non-specific requests',
        },
        {
          category: 'Double Booking Prevention',
          rule: 'Iris never double books',
          explanation: 'System maintains strict scheduling integrity to ensure quality of care',
        },
        {
          category: 'Current Limitations',
          rule: 'Iris cannot book families or multiple patients in 1 call today',
          explanation: 'Single patient per call ensures accuracy and reduces complexity',
        },
      ],
    },
  };

  const handleExport = (format: string, content: string) => {
    // Placeholder function - doesn't actually export
    console.log(`Exporting ${content} as ${format}`);
  };

  return (
    <div className="space-y-6">
      {/* Header with Configuration Controls */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold">How Iris Makes Scheduling Decisions</h2>
          <p className="text-sm text-muted-foreground">
            Common scenarios and troubleshooting for scheduling logic •{' '}
            {configMode ? 'Configuration mode' : 'View mode'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={configMode ? 'default' : 'outline'}
            size="sm"
            onClick={() => setConfigMode(!configMode)}
            className={configMode ? 'bg-blue-600 hover:bg-blue-700' : ''}
          >
            <Settings className="h-4 w-4 mr-2" />
            {configMode ? 'Exit Config' : 'Configure Rules'}
          </Button>
          {configMode && (
            <Button variant="outline" size="sm" disabled>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          )}
        </div>
      </div>

      {/* Configuration Mode Banner */}
      {configMode && (
        <div className="rounded-lg border-l-4 border-l-blue-500 bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">Configuration Mode Active</h3>
              <p className="text-sm text-blue-700 mt-1">
                You can view and understand the structure of scheduling rules. Click on any rule to
                see editing affordances. Changes to these rules would affect how Iris makes
                scheduling decisions across all patient interactions.
              </p>
              <div className="mt-2 text-xs text-blue-600 font-medium">
                🔧 Rules are editable • 📝 Categories can be reorganized • ⚡ Changes apply
                immediately
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-green-600">{schedulingStats.schedulable}</p>
                <p className="text-sm text-muted-foreground">Schedulable Types</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {Math.round((schedulingStats.schedulable / schedulingStats.total) * 100)}% of all
              appointment types
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-red-600">{schedulingStats.nonSchedulable}</p>
                <p className="text-sm text-muted-foreground">Require Transfer</p>
              </div>
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {Math.round((schedulingStats.nonSchedulable / schedulingStats.total) * 100)}% need
              manual scheduling
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{schedulingStats.total}</p>
                <p className="text-sm text-muted-foreground">Total Types</p>
              </div>
              <Users className="h-8 w-8 text-gray-600" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Across all providers and specialties
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Task-Oriented Quick Actions with Detailed Rules */}
      <div className="space-y-4">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="cursor-pointer" onClick={() => toggleSection('troubleshoot')}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-red-100 text-red-600 flex items-center justify-center">
                <HelpCircle className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-base">Troubleshoot Scheduling Issues</h3>
                  {expandedSections.troubleshoot ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  "Why didn't Iris schedule this patient?" - Understand why appointments get
                  transferred to staff
                </p>
                <div className="text-xs text-muted-foreground">
                  • Check appointment type configuration • Verify provider permissions • Review time
                  slot constraints
                </div>
              </div>
            </div>
          </CardHeader>

          {expandedSections.troubleshoot && (
            <CardContent className="pt-0">
              <div className="space-y-3">
                {detailedRules.troubleshoot.rules.map((rule, index) => (
                  <div
                    key={`troubleshoot-${rule.category}-${index}`}
                    className={`p-3 rounded-lg transition-all ${
                      configMode
                        ? 'bg-muted/30 border border-dashed border-muted-foreground/30 hover:border-red-300 hover:bg-red-50/50 cursor-pointer'
                        : 'bg-muted/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs font-semibold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-medium text-sm">{rule.category}</h4>
                          {configMode && (
                            <div className="flex items-center gap-1 opacity-60 hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled>
                                <Edit3 className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled>
                                <MoreHorizontal className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <p
                          className={`text-sm mb-2 ${configMode ? 'text-foreground font-medium' : 'text-foreground'}`}
                        >
                          {rule.rule}
                        </p>
                        <p className="text-xs text-muted-foreground">{rule.explanation}</p>
                        {configMode && (
                          <div className="mt-2 pt-2 border-t border-dashed border-muted-foreground/20">
                            <p className="text-xs text-muted-foreground italic">
                              Click to edit rule logic and conditions
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="cursor-pointer" onClick={() => toggleSection('capacity')}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                <TrendingUp className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-base">Review Scheduling Capacity</h3>
                  {expandedSections.capacity ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  Understand what Iris can currently handle vs. what requires manual intervention
                </p>
                <div className="text-xs text-muted-foreground">
                  • View schedulable vs non-schedulable breakdown • Identify opportunities for
                  automation • Track success rates
                </div>
              </div>
            </div>
          </CardHeader>

          {expandedSections.capacity && (
            <CardContent className="pt-0">
              <div className="space-y-3">
                {detailedRules.capacity.rules.map((rule, index) => (
                  <div
                    key={`capacity-${rule.category}-${index}`}
                    className={`p-3 rounded-lg transition-all ${
                      configMode
                        ? 'bg-muted/30 border border-dashed border-muted-foreground/30 hover:border-blue-300 hover:bg-blue-50/50 cursor-pointer'
                        : 'bg-muted/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-semibold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-medium text-sm">{rule.category}</h4>
                          {configMode && (
                            <div className="flex items-center gap-1 opacity-60 hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled>
                                <Edit3 className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled>
                                <MoreHorizontal className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <p
                          className={`text-sm mb-2 ${configMode ? 'text-foreground font-medium' : 'text-foreground'}`}
                        >
                          {rule.rule}
                        </p>
                        <p className="text-xs text-muted-foreground">{rule.explanation}</p>
                        {configMode && (
                          <div className="mt-2 pt-2 border-t border-dashed border-muted-foreground/20">
                            <p className="text-xs text-muted-foreground italic">
                              Click to edit rule logic and conditions
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="cursor-pointer" onClick={() => toggleSection('configuration')}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
                <Settings className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-base">Verify Rule Configuration</h3>
                  {expandedSections.configuration ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  Check if scheduling rules and appointment types are configured correctly
                </p>
                <div className="text-xs text-muted-foreground">
                  • Review appointment type settings • Check provider-specific permissions •
                  Validate scheduling constraints
                </div>
              </div>
            </div>
          </CardHeader>

          {expandedSections.configuration && (
            <CardContent className="pt-0">
              <div className="space-y-3">
                {detailedRules.configuration.rules.map((rule, index) => (
                  <div
                    key={`configuration-${rule.category}-${index}`}
                    className={`p-3 rounded-lg transition-all ${
                      configMode
                        ? 'bg-muted/30 border border-dashed border-muted-foreground/30 hover:border-green-300 hover:bg-green-50/50 cursor-pointer'
                        : 'bg-muted/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs font-semibold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-medium text-sm">{rule.category}</h4>
                          {configMode && (
                            <div className="flex items-center gap-1 opacity-60 hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled>
                                <Edit3 className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled>
                                <MoreHorizontal className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <p
                          className={`text-sm mb-2 ${configMode ? 'text-foreground font-medium' : 'text-foreground'}`}
                        >
                          {rule.rule}
                        </p>
                        <p className="text-xs text-muted-foreground">{rule.explanation}</p>
                        {configMode && (
                          <div className="mt-2 pt-2 border-t border-dashed border-muted-foreground/20">
                            <p className="text-xs text-muted-foreground italic">
                              Click to edit rule logic and conditions
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="cursor-pointer" onClick={() => toggleSection('investigation')}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
                <Search className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-base">Investigate Specific Types</h3>
                  {expandedSections.investigation ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  Deep dive into why specific appointment types behave unexpectedly
                </p>
                <div className="text-xs text-muted-foreground">
                  • Look up specific appointment types • Understand complex scheduling rules • Check
                  provider patterns
                </div>
              </div>
            </div>
          </CardHeader>

          {expandedSections.investigation && (
            <CardContent className="pt-0">
              <div className="space-y-3">
                {detailedRules.investigation.rules.map((rule, index) => (
                  <div
                    key={`investigation-${rule.category}-${index}`}
                    className={`p-3 rounded-lg transition-all ${
                      configMode
                        ? 'bg-muted/30 border border-dashed border-muted-foreground/30 hover:border-purple-300 hover:bg-purple-50/50 cursor-pointer'
                        : 'bg-muted/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-semibold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-medium text-sm">{rule.category}</h4>
                          {configMode && (
                            <div className="flex items-center gap-1 opacity-60 hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled>
                                <Edit3 className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled>
                                <MoreHorizontal className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <p
                          className={`text-sm mb-2 ${configMode ? 'text-foreground font-medium' : 'text-foreground'}`}
                        >
                          {rule.rule}
                        </p>
                        <p className="text-xs text-muted-foreground">{rule.explanation}</p>
                        {configMode && (
                          <div className="mt-2 pt-2 border-t border-dashed border-muted-foreground/20">
                            <p className="text-xs text-muted-foreground italic">
                              Click to edit rule logic and conditions
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Simplified Decision Flow */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowRight className="h-5 w-5" />
            How Iris Decides What to Schedule
          </CardTitle>
          <CardDescription>
            The key checkpoints Iris uses when a patient requests an appointment
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                1
              </div>
              <div className="flex-1">
                <h4 className="font-semibold mb-1">Is the appointment type enabled?</h4>
                <p className="text-sm text-muted-foreground">
                  Only {schedulingStats.schedulable} of {schedulingStats.total} appointment types
                  are currently enabled for Iris scheduling
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                2
              </div>
              <div className="flex-1">
                <h4 className="font-semibold mb-1">Can this provider handle it?</h4>
                <p className="text-sm text-muted-foreground">
                  Some appointment types have provider-specific restrictions (e.g., cosmetic
                  procedures for Dr. Doshi only)
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                3
              </div>
              <div className="flex-1">
                <h4 className="font-semibold mb-1">Are there available time slots?</h4>
                <p className="text-sm text-muted-foreground">
                  Iris checks the provider's schedule and applies duration-based constraints (e.g.,
                  no back-to-back long appointments)
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-6">
              <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="font-semibold text-green-900">
                    All checks pass → Iris schedules
                  </span>
                </div>
                <p className="text-sm text-green-700">
                  Patient gets immediate confirmation and appointment details
                </p>
              </div>

              <div className="p-4 rounded-lg bg-orange-50 border border-orange-200">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                  <span className="font-semibold text-orange-900">Any check fails → Transfer</span>
                </div>
                <p className="text-sm text-orange-700">
                  Patient is smoothly transferred to office staff for manual scheduling
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export and Configuration Controls */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          {configMode ? (
            <span className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              {Object.keys(detailedRules).reduce(
                (total, key) =>
                  total + detailedRules[key as keyof typeof detailedRules].rules.length,
                0
              )}{' '}
              configurable rules across 4 categories
            </span>
          ) : (
            <span>Mental model documentation and rule reference</span>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              {configMode ? 'Export Configuration' : 'Export Mental Model'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleExport('pdf', 'full-mental-model')}>
              <FileText className="h-4 w-4 mr-2" />
              Complete Guide (PDF)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('png', 'decision-flow')}>
              <Image className="h-4 w-4 mr-2" />
              Decision Flow (PNG)
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {configMode && (
              <>
                <DropdownMenuItem
                  onClick={() => handleExport('json', 'rule-configuration')}
                  disabled
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Rule Configuration (JSON)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('csv', 'rule-changes')} disabled>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Recent Changes (CSV)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={() => handleExport('json', 'rules-data')}>
              <Share className="h-4 w-4 mr-2" />
              Rules Data (JSON)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
